import { ShopCore } from "@/abilities/shop/types/shop-core";
import { ShopItemInfo, StockStatus } from "@/shared/services/graphql/generated/consumer-graph-types";

/**
 * Prices arrive as an integer minor unit (QIT-6923); the divisor comes from
 * `Intl.NumberFormat` since minor-unit digits vary by currency (JPY 0, EUR 2,
 * KWD 3). The locale here is arbitrary — digit count is locale-independent;
 * user-facing formatting uses `getUiLanguage()` elsewhere.
 */
function minorUnitDigits(currencyCode: string): number {
    try {
        // TS types this optional; with style: "currency" it's always set for a
        // valid code, but the `?? 2` satisfies the compiler either way.
        return (
            new Intl.NumberFormat("en", { style: "currency", currency: currencyCode }).resolvedOptions()
                .maximumFractionDigits ?? 2
        );
    } catch {
        return 2;
    }
}

function toMajorUnits(minorAmount: number, currencyCode: string): number {
    return minorAmount / 10 ** minorUnitDigits(currencyCode);
}

/**
 * The middleware owns currency display (QIT-7389): its sign, else its code,
 * else nothing. The frontend never turns a code into a symbol here. Always a
 * string: `show-price-with-currency.vue` hides the whole price when the
 * currency is `undefined`.
 */
function currencyDisplayText(currency: ShopItemInfo["price"]["currency"]): string {
    return currency.sign?.trim() || currency.code || "";
}

/**
 * What the widget already reads as "quantity unknown" — `availability-indicator.vue`
 * renders no number for it. The generic shop reports an omitted quantity as
 * `stock.stock: null`, which must not collapse to `0`: that would render as
 * "0 in stock" for an article the shop calls in stock.
 */
export const UNKNOWN_QUANTITY = -1;

/**
 * The backend's stock taxonomy is wider than the widget's. Written as a total
 * `Record`, so a new `StockStatus` member added backend-side fails the build
 * here instead of silently falling into a default branch.
 *
 * `DISCONTINUED` and `UNKNOWN` both map to `UNAVAILABLE`: neither is a state
 * the widget can present as a stock level, and `UNAVAILABLE` is the one that
 * renders as a grey dot without a quantity.
 */
const STOCK_STATUS_MAP: Record<StockStatus, ShopCore.StockStatus> = {
    [StockStatus.inStock]: ShopCore.StockStatus.AVAILABLE,
    [StockStatus.lowStock]: ShopCore.StockStatus.LOW_STOCK,
    [StockStatus.outOfStock]: ShopCore.StockStatus.OUT_OF_STOCK,
    [StockStatus.unavailable]: ShopCore.StockStatus.UNAVAILABLE,
    [StockStatus.discontinued]: ShopCore.StockStatus.UNAVAILABLE,
    [StockStatus.unknown]: ShopCore.StockStatus.UNAVAILABLE,
};

/**
 * Normalizes one Generic Shop Interface `ShopItemInfo` into the `WidgetItem`
 * the shop widget renders (QIT-7074). This is the only place the generic
 * contract meets the widget's vocabulary.
 *
 * Three decisions worth knowing, recorded in `docs/work-items/QIT-7074` and
 * `docs/work-items/QIT-7389`:
 *
 * - **The currency text is the middleware's, shown on both lines.** Its sign if
 *   it sent one, else its code (QIT-7389, replacing the QIT-7074 rule that made
 *   the symbol from the code). A missing or wrong sign
 *   (`ART-CURRENCY-SIGN-OMITTED`, `ART-CURRENCY-SIGN-MISMATCH`) shows as it is.
 *   `currency` and `currencySign` get the same text, so the price line and the
 *   total line cannot differ.
 * - **`priceOnRequest: null` means "not on request".** The field is nullable and
 *   the mock leaves it null for most static articles while the hash fallback
 *   sends a real boolean; the two are not meant to differ.
 * - **Values are passed through, not corrected.** Negative prices and negative
 *   stock reach the widget as they arrive — clamping them would be exactly the
 *   kind of frontend shop rule QIT-7073 removes.
 *
 * The price amount arrives as an integer minor unit and is converted to a
 * major unit here (QIT-6923), so the widget always renders a major-unit
 * value regardless of the currency's decimal precision.
 */
export function getWidgetItemFromShopItemInfo(info: ShopItemInfo): ShopCore.WidgetItem {
    const currencyText = currencyDisplayText(info.price.currency);
    return {
        price: {
            amount: info.price.price != null ? toMajorUnits(info.price.price, info.price.currency.code) : null,
            currency: currencyText,
            currencySign: currencyText,
            onRequestOnly: info.priceOnRequest ?? false,
        },
        stock: {
            available: STOCK_STATUS_MAP[info.stock.stockStatus],
            quantity: info.stock.stock ?? UNKNOWN_QUANTITY,
        },
        orderable: info.orderable,
    };
}
